import React, {useCallback, useEffect, useRef, useState} from 'react';

import {Loader} from '@googlemaps/js-api-loader';
import {GoogleMap as GM, Marker, OverlayView} from '@react-google-maps/api';
import {format, isValid, parseISO} from 'date-fns';

import {useStore} from 'effector-react';

import {getClusters} from 'api/map';
import {ReactComponent as CrossIcon} from 'assets/svg/cross.svg';

import {Icon} from 'components/ui-kit/Icon';
import {COUNTRIES, COUNTRY} from 'core/constants/env';
import {dashboardState$} from 'models/dashboard';
import {colors} from 'theme';
import {Cluster, clusterEvents, ClustersRequest} from 'types/geo';

import {MapWrapper, Popup} from './styled';

const containerStyle = {width: '100%', height: '500px'};

const CENTERS: Record<COUNTRIES, {lat: number; lng: number}> = {
    CAR: {lat: 6.611111, lng: 20.939444},
    NG: {lat: 9.082, lng: 8.6753},
    TJ: {lat: 38.861, lng: 71.2761},
};

const CENTER = CENTERS[COUNTRY];
const ZOOM = 6;

const loader = new Loader({
    apiKey: process.env.REACT_APP_GOOGLE_MAPS_KEY ?? '',
    version: 'weekly',
    language: 'en',
});

export const GoogleMap = () => {
    const {dateRange} = useStore(dashboardState$);
    const [loaded, setGoogleLoaded] = useState(false);
    const [error, setError] = useState(false);
    const mapRef = useRef<google.maps.Map | null>(null);
    const boundsRef = useRef<[number, number, number, number] | null>(null);
    const debounceTimer = useRef<number | null>(null);

    const [clusters, setClusters] = useState<Cluster[]>([]);
    const [selectedCluster, setSelectedCluster] = useState<Cluster | null>(null);

    useEffect(() => {
        let canceled = false;
        loader
            .importLibrary('maps')
            .then(() => {
                if (!canceled) setGoogleLoaded(true);
            })
            .catch((e) => {
                console.error(e);
                setError(true);
            });
        return () => {
            canceled = true;
        };
    }, []);

    useEffect(() => {
        fetchClusters();
    }, [dateRange]);

    const DATE_PATTERN = "yyyy-MM-dd'T'HH:mm:ss.SSS'";
    const fetchClusters = async () => {
        if (!mapRef.current || !boundsRef.current) return;
        const payload: ClustersRequest = {
            zoomLevel: mapRef.current.getZoom() ?? ZOOM,
            // zoomLevel: 22,
            bounds: boundsRef.current.map((n) => n.toFixed(6)).join(','),
            eventTypes: clusterEvents.join(','),
            startDate: format(dateRange.start, DATE_PATTERN),
            endDate: format(dateRange.end, DATE_PATTERN),
        };

        try {
            const updatedClusters = await getClusters(payload);
            setClusters(updatedClusters);
            setSelectedCluster(null);
        } catch (err) {
            console.error('Error', err);
        }
    };

    const formatNumberWithFraction = (v?: any, options: Intl.NumberFormatOptions = {}) => {
        if (!['number', 'string'].includes(typeof v)) return '—';
        return Number(v).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            ...options,
        });
    };

    const formatShort = (number: number, formatOptions?: Intl.NumberFormatOptions) => {
        const n = Number(number);
        if (isNaN(n)) return '—';

        const options = {
            notation: 'compact',
            compactDisplay: 'short',
            maximumFractionDigits: 2,
            ...(formatOptions ?? {}),
        };
        return n.toLocaleString('en-US', options);
    };

    function formatCompact5(n: number): string {
        let formatted = formatShort(n);
        if (formatted.length <= 5) return formatted;

        formatted = formatShort(n, {maximumFractionDigits: 1});
        if (formatted.length <= 5) return formatted;

        formatted = formatShort(n, {maximumFractionDigits: 0});
        if (formatted.length <= 5) return formatted;

        const suffix = formatted.replace(/[0-9.,]/g, '');
        return formatted.slice(0, 5 - suffix.length) + suffix;
    }

    const onMapLoad = useCallback(
        (map: google.maps.Map) => {
            mapRef.current = map;

            map.addListener('bounds_changed', () => {
                if (!mapRef.current) return;
                const bounds = mapRef.current.getBounds();
                if (!bounds) return;

                const sw = bounds.getSouthWest();
                const ne = bounds.getNorthEast();
                boundsRef.current = [sw.lat(), sw.lng(), ne.lat(), ne.lng()];

                if (debounceTimer.current) clearTimeout(debounceTimer.current);
                debounceTimer.current = window.setTimeout(fetchClusters, 500);
            });
        },
        [fetchClusters],
    );

    if (!loaded || error) return null;

    const MAX_SCALE = 1;
    const MIN_SCALE = 0.66;
    const BASE_SIZE = 88;
    const BASE_FONT_SIZE = 20;

    const maxEventsCount = Math.max(...clusters.map((c) => c.eventsCount));
    const getScaleByEvents = (eventsCount: number) => {
        if (!maxEventsCount) return MAX_SCALE;

        const normalized = Math.sqrt(eventsCount / maxEventsCount);
        return MIN_SCALE + (MAX_SCALE - MIN_SCALE) * normalized;
    };

    const getClusterIcon = (scale: number) => {
        const size = BASE_SIZE * scale;
        const r1 = 44 * scale;
        const r2 = 35 * scale;
        const r3 = 30 * scale;

        const svg = `
<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
  <circle cx="${size / 2}" cy="${size / 2}" r="${r1}" fill="${colors.brandPrimary}59" />
  <circle cx="${size / 2}" cy="${size / 2}" r="${r2}" fill="white" />
  <circle cx="${size / 2}" cy="${size / 2}" r="${r3}" fill="${colors.brandPrimary}" />
</svg>
`;

        return {
            url: 'data:image/svg+xml;base64,' + btoa(svg),
            scaledSize: new google.maps.Size(size, size),
            anchor: new google.maps.Point(size / 2, size / 2),
        };
    };

    return (
        <MapWrapper>
            <GM
                mapContainerStyle={containerStyle}
                center={CENTER}
                zoom={ZOOM}
                onLoad={onMapLoad}
                onClick={() => setSelectedCluster(null)}
            >
                {clusters.map((c, i) => {
                    const scale = getScaleByEvents(c.eventsCount);
                    const icon = getClusterIcon(scale);

                    return (
                        <Marker
                            key={i}
                            position={{lat: c.centerLatitude, lng: c.centerLongitude}}
                            icon={icon}
                            label={{
                                text: formatCompact5(c.eventsCount),
                                color: 'white',
                                fontSize: `${Math.round(BASE_FONT_SIZE * scale)}px`,
                                fontWeight: 'bold',
                            }}
                            onClick={() => setSelectedCluster(c)}
                        />
                    );
                })}

                {selectedCluster && (
                    <OverlayView
                        position={{
                            lat: selectedCluster.centerLatitude,
                            lng: selectedCluster.centerLongitude,
                        }}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                        <Popup>
                            <Icon
                                source={CrossIcon}
                                onClick={() => setSelectedCluster(null)}
                                width={12}
                                height={12}
                            />
                            <div>
                                <span>Number of codes:</span>
                                <div>{formatNumberWithFraction(selectedCluster?.eventsCount)}</div>
                            </div>

                            <div>
                                <span>Event types:</span>
                                <div>{selectedCluster?.eventTypes.join(', ')}</div>
                            </div>

                            <div>
                                <span>Scan date:</span>
                                <div>
                                    {isValid(parseISO(selectedCluster?.lastEventTime ?? ''))
                                        ? format(
                                              new Date(
                                                  parseISO(selectedCluster?.lastEventTime ?? ''),
                                              ),
                                              'dd-MM-yyyy, HH:mm',
                                          )
                                        : '—'}
                                </div>
                            </div>
                        </Popup>
                    </OverlayView>
                )}
            </GM>
        </MapWrapper>
    );
};
